import { dailyCallStore } from 'features/daily-call/model';
import { useEffect, useRef, useState } from 'react';
import { cn, useMouseVisibility, useQueryParams, logger } from 'shared/lib';
import { TalkingMode } from './TalkingMode';
import { UserVideo, useWebcam } from 'features/recognize-emotions';
import TavusEmbedCallSettings from './TavusEmbedCallSettings';
import { hotkeyDescriptions, useKeyboardBindings } from '../lib';
import { CallEndModal } from 'features/call-end';
import { ToolTip } from 'shared/ui';
import {
  EIntegrationSettingsType,
  tavusPersonaStore
} from 'entities/tavus/persona';
import { Call, Video, VideoSlash } from 'iconsax-reactjs';
import { LuMonitor, LuMonitorOff } from 'react-icons/lu';
import { ScreenCast } from 'features/recognize-emotions/ui/ScreenCast';
import { TbKeyboard, TbKeyboardOff } from 'react-icons/tb';
import { getPermissionValue, Permissions } from 'shared/lib/getPermissionValue';
import PoweredBy from 'widgets/avatar-edit-persona-layout/ui/PoweredBy';

type Props = {
  onCallEnd: () => void;
};

export const TavusEmbedControlBar = ({ onCallEnd }: Props) => {
  const [open, setOpen] = useState(false);
  const {
    callObject,
    isRecordingMic,
    startMic,
    stopMic,
    withCaptions,
    toggleCaptions,
    setWithCaptions
    // isRecording,
    // startRecording,
    // stopRecording
  } = dailyCallStore();

  const query = useQueryParams();
  const type = query.get('type') as EIntegrationSettingsType;
  const disableFeatures = query.get('disableFeatures') === 'true';

  const currentPersona = tavusPersonaStore((state) => state.currentPersona);
  const windowFields =
    type === 'embed' ? currentPersona?.embed : currentPersona?.widget;

  const { startWebcam, stopWebcam, webcamLoading, isWebcam } = useWebcam();
  const webcamRef = useRef<HTMLVideoElement>(null);
  const screencastRef = useRef<HTMLVideoElement>(null);
  const [isScreencast, setIsScreencast] = useState(false);
  const [screencastStream, setScreencastStream] = useState<MediaStream | null>(
    null
  );

  const toggleMic = () => {
    isRecordingMic ? stopMic() : startMic();
  };

  const stopScreencast = async () => {
    if (screencastRef.current?.srcObject) {
      (screencastRef.current.srcObject as MediaStream)
        .getTracks()
        .forEach((track) => track.stop());
      screencastRef.current.srcObject = null;
    }
    setScreencastStream(null);
    setIsScreencast(false);
  };

  const startScreencast = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      alert('Your browser does not support screen capture.');
      return;
    }

    try {
      stopWebcam();

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 60 },
        audio: false
      });

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) return;

      await callObject?.setInputDevicesAsync({ videoSource: videoTrack });
      setScreencastStream(stream);
      setIsScreencast(true);
    } catch (err) {
      logger.error('Failed to start screencast:', err);
      setIsScreencast(false);
    }
  };

  const startWebcamHandler = async () => {
    if (isScreencast) {
      await stopScreencast();
    }

    if (webcamRef.current) {
      await startWebcam(webcamRef.current);
    }

    setIsScreencast(false);
  };

  const toggleVideo = async () => {
    if (isWebcam) {
      stopWebcam();
    } else {
      await startWebcamHandler();
    }
  };

  useKeyboardBindings({
    toggleMic,
    toggleWebcam: toggleVideo,
    setOpen
  });

  useEffect(() => {
    if (
      disableFeatures ||
      getPermissionValue({ permissionKey: Permissions.MICROPHONE }) === true
    ) {
      callObject?.setLocalAudio(false);
    } else {
      callObject?.setLocalAudio(isRecordingMic);
    }
  }, [isRecordingMic, callObject, disableFeatures]);

  useEffect(() => {
    callObject?.setLocalVideo(isWebcam || isScreencast);
  }, [isWebcam, isScreencast]);

  useEffect(() => {
    if (screencastRef.current && isScreencast && screencastStream) {
      const video = screencastRef.current;
      video.srcObject = screencastStream;
      video.onloadedmetadata = () => {
        video
          .play()
          // @ts-ignore
          .catch((err) => logger.warn('Autoplay screencast failed', err));
      };
    }
  }, [isScreencast, screencastRef.current, screencastStream]);

  useEffect(() => {
    return () => {
      stopWebcam();
      stopScreencast();
    };
  }, []);

  useEffect(() => {
    if (windowFields?.captions_by_default) {
      setWithCaptions(true);
    } else {
      setWithCaptions(false);
    }
  }, [windowFields]);

  const isVisible = useMouseVisibility();

  return (
    <div
      className={cn(
        'ag-border-t ag-flex ag-flex-col ag-justify-between ag-flex-shrink-0 ag-w-full ag-text-white',
        windowFields?.dark_mode
          ? 'ag-border-neutral-600'
          : 'ag-border-neutral-200'
      )}
    >
      <div
        className={`ag-duration-200 ${
          isVisible ? 'lg:-ag-translate-y-10' : 'lg:ag-translate-y-0'
        }`}
      >
        {getPermissionValue({
          permissionKey: Permissions.SCREEN_SHARE
        }) !== true && isScreencast ? (
          <ScreenCast ref={screencastRef} />
        ) : (
          <UserVideo ref={webcamRef} />
        )}
      </div>

      <div
        className={`ag-absolute ag-left-0 ag-duration-300 ag-right-0 ${
          isVisible ? 'ag-bottom-10' : '-ag-bottom-full'
        }`}
      >
        <div
          className={cn(
            'ag-flex ag-gap-4 ag-py-4 ag-px-6 ag-items-center ag-justify-center',
            windowFields?.dark_mode ? 'ag-bg-neutral-900' : ''
          )}
        >
          {!disableFeatures ? (
            <>
              {getPermissionValue({
                permissionKey: Permissions.CAPTIONS
              }) !== true && (
                <button
                  onClick={toggleCaptions}
                  className="ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
                >
                  {withCaptions ? (
                    <TbKeyboard size="24" color="currentColor" />
                  ) : (
                    <TbKeyboardOff size="24" color="#DC2626" />
                  )}
                </button>
              )}
              {/* <button
                onClick={isRecording ? stopRecording : startRecording}
                className="ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
                title={isRecording ? 'Stop recording' : 'Start recording'}
              >
                <div
                  className={`ag-mx-auto ag-h-4 ag-w-4 ag-rounded-full ${
                    isRecording ? 'ag-bg-red-600' : 'ag-border ag-border-white'
                  }`}
                />
                <ToolTip
                  topClass="-ag-top-10"
                  text={isRecording ? 'Stop recording' : 'Start recording'}
                />
              </button> */}
              {getPermissionValue({ permissionKey: Permissions.CAMERA }) !==
                true &&
                windowFields?.camera === true && (
                  <button
                    onClick={toggleVideo}
                    disabled={webcamLoading}
                    className="ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
                  >
                    {isWebcam ? (
                      <Video size="24" color="currentColor" />
                    ) : (
                      <VideoSlash size="24" color="#DC2626" />
                    )}
                    <ToolTip
                      topClass="-ag-top-10"
                      text={hotkeyDescriptions.toggleWebcam}
                    />
                  </button>
                )}

              {getPermissionValue({
                permissionKey: Permissions.SCREEN_SHARE
              }) !== true &&
                windowFields?.screen_share === true && (
                  <button
                    onClick={isScreencast ? stopScreencast : startScreencast}
                    className="ag-rounded-[10px] ag-group ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
                  >
                    {isScreencast ? (
                      <LuMonitor size="24" color="currentColor" />
                    ) : (
                      <LuMonitorOff size="24" color="#DC2626" />
                    )}
                    <ToolTip topClass="-ag-top-10" text="Toggle Screencast" />
                  </button>
                )}

              {getPermissionValue({
                permissionKey: Permissions.MICROPHONE
              }) !== true && (
                <div className="ag-relative group">
                  <TalkingMode
                    startMicrophone={startMic}
                    stopMicrophone={stopMic}
                    isRecordingMic={isRecordingMic}
                  />
                  <ToolTip
                    topClass="-ag-top-10"
                    text={hotkeyDescriptions.toggleMic}
                  />
                </div>
              )}
            </>
          ) : (
            <></>
          )}
          {getPermissionValue({
            permissionKey: Permissions.END_CALL
          }) !== true && (
            <button
              onClick={onCallEnd}
              className="ag-h-10 ag-w-10 hover:ag-opacity-80 ag-duration-300 ag-bg-button-red ag-text-white ag-py-2 ag-rounded-full ag-flex ag-flex-row ag-justify-center ag-items-center"
            >
              <Call color="currentColor" size="24" />
              <ToolTip
                topClass="-ag-top-10"
                text={hotkeyDescriptions.endCall}
              />
            </button>
          )}
        </div>
        {windowFields?.powered_by && (
          <div className="ag-flex ag-flex-col ag-justify-center ag-items-center">
            <PoweredBy isPoweredBy={windowFields?.powered_by} />
          </div>
        )}
      </div>

      <CallEndModal onCallEnd={onCallEnd} open={open} setOpen={setOpen} />
    </div>
  );
};
